import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Flag, RotateCcw } from "lucide-react";

function formatDate(d: string | Date | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function hoursSince(d: string | Date) {
  return Math.floor((Date.now() - new Date(d).getTime()) / (1000 * 60 * 60));
}

export default function OpsNoShows() {
  const { user } = useAuth();
  const hasWrite = canWrite(user?.staffRole || null);

  const [minHours, setMinHours] = useState(72);

  // Flag dialog state
  const [flagMatchId, setFlagMatchId] = useState<string | null>(null);
  const [flagReason, setFlagReason] = useState("");

  // Refund dialog state
  const [refundMatchId, setRefundMatchId] = useState<{ id: string; companyName: string } | null>(null);
  const [refundReason, setRefundReason] = useState("");

  const { data, isLoading, isError, refetch } = (trpc.admin as any).opsLeadOps.listNoShowMatches.useQuery(
    { minHoursSinceUnlock: minHours, limit: 100 }
  );

  const items: any[] = data?.items ?? [];

  const flagMutation = (trpc.admin as any).opsLeadOps.flagAsNoShow.useMutation({
    onSuccess: () => {
      toast.success("Match flaggé no-show");
      setFlagMatchId(null);
      setFlagReason("");
      refetch();
    },
    onError: (err: any) => toast.error(err?.message ?? "Erreur"),
  });

  const refundMutation = (trpc.admin as any).opsLeadOps.manualRefund.useMutation({
    onSuccess: () => {
      toast.success("Crédit remboursé");
      setRefundMatchId(null);
      setRefundReason("");
      refetch();
    },
    onError: (err: any) => toast.error(err?.message ?? "Erreur"),
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-warning" />
              No-shows artisans
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Artisans qui ont débloqué un lead mais n'ont jamais contacté le particulier (0 appel, 0 message).
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraîchir
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Candidats no-show</CardTitle>
              <div className="flex items-center gap-2">
                <Label htmlFor="min-hours" className="text-xs whitespace-nowrap">
                  Heures depuis unlock ≥
                </Label>
                <Input
                  id="min-hours"
                  type="number"
                  min={1}
                  max={720}
                  value={minHours}
                  onChange={(e) => setMinHours(Math.max(1, Number(e.target.value) || 72))}
                  className="w-20 h-8"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-8 text-center">Chargement…</div>
            ) : isError ? (
              <div className="text-sm text-destructive py-8 text-center">Erreur de chargement</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Aucun no-show détecté avec ces critères.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Projet</TableHead>
                    <TableHead>Artisan</TableHead>
                    <TableHead>Particulier</TableHead>
                    <TableHead>Débloqué</TableHead>
                    <TableHead className="text-center">Crédits</TableHead>
                    {hasWrite && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="text-sm">
                        <div className="font-medium">{m.PrivateProject?.title ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {m.PrivateProject?.category} · {m.PrivateProject?.city ?? "—"}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {m.Company?.name ?? m.companyId}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>
                          {[m.PrivateProject?.CreatedBy?.firstName, m.PrivateProject?.CreatedBy?.lastName]
                            .filter(Boolean)
                            .join(" ") || m.PrivateProject?.CreatedBy?.email || "—"}
                        </div>
                        {m.PrivateProject?.CreatedBy?.phone && (
                          <div className="text-xs text-muted-foreground">
                            {m.PrivateProject.CreatedBy.phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{formatDate(m.unlockedAt)}</div>
                        <div className="text-xs text-muted-foreground">
                          il y a {hoursSince(m.unlockedAt)}h
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm tabular-nums">
                        <Badge variant="outline">{m.creditPricePaid ?? 1}</Badge>
                      </TableCell>
                      {hasWrite && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setFlagMatchId(m.id);
                                setFlagReason("");
                              }}
                            >
                              <Flag className="h-3.5 w-3.5 mr-1" />
                              Flagger
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setRefundMatchId({
                                  id: m.id,
                                  companyName: m.Company?.name ?? m.companyId,
                                });
                                setRefundReason("");
                              }}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-1" />
                              Refund au particulier
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Flag dialog */}
      <Dialog
        open={!!flagMatchId}
        onOpenChange={(open) => {
          if (!open) {
            setFlagMatchId(null);
            setFlagReason("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Flagger comme no-show</DialogTitle>
            <DialogDescription>
              Marque le match comme no-show. L'audit log enregistre l'action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="flag-reason">Raison</Label>
            <Textarea
              id="flag-reason"
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              rows={4}
              placeholder="Pourquoi ce match est-il un no-show ?"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setFlagMatchId(null);
                setFlagReason("");
              }}
              disabled={flagMutation.isLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (!flagMatchId) return;
                flagMutation.mutate({ matchId: flagMatchId, reason: flagReason.trim() });
              }}
              disabled={flagMutation.isLoading || flagReason.trim().length < 3}
            >
              {flagMutation.isLoading ? "En cours…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog
        open={!!refundMatchId}
        onOpenChange={(open) => {
          if (!open) {
            setRefundMatchId(null);
            setRefundReason("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Refund manuel</DialogTitle>
            <DialogDescription>
              Re-créditer {refundMatchId?.companyName ?? "l'entreprise"}. Action tracée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="refund-reason">Raison</Label>
            <Textarea
              id="refund-reason"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              rows={4}
              placeholder="Justification du remboursement…"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRefundMatchId(null);
                setRefundReason("");
              }}
              disabled={refundMutation.isLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (!refundMatchId) return;
                refundMutation.mutate({ matchId: refundMatchId.id, reason: refundReason.trim() });
              }}
              disabled={refundMutation.isLoading || refundReason.trim().length < 3}
            >
              {refundMutation.isLoading ? "En cours…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
